/**
 * useFocusTrap.js
 * Custom hook for trapping focus within modal dialogs
 *
 * WCAG 2.1 Level A Requirement:
 * When a modal is open, keyboard users (Tab/Shift+Tab) should only
 * be able to cycle through focusable elements inside the modal.
 * Focus should not escape to the page behind the modal.
 *
 * @param {React.RefObject} modalRef - Ref to the modal container element
 * @param {boolean} isActive - Whether the focus trap is currently active
 * @param {Object} options - Additional options
 * @param {boolean} options.returnFocus - Whether to return focus on deactivate (default: true)
 */

import { useEffect, useRef, useCallback } from 'react';

// =========================================
// Focusable Element Selector
// =========================================
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])'
].join(', ');

const useFocusTrap = (modalRef, isActive, options = {}) => {
  const { returnFocus = true } = options;

  // Store the element that was focused before the modal opened
  const previouslyFocusedElement = useRef(null);

  // =========================================
  // Get Focusable Elements
  // =========================================
  const getFocusableElements = useCallback(() => {
    if (!modalRef.current) return [];

    const elements = modalRef.current.querySelectorAll(FOCUSABLE_SELECTOR);
    return Array.from(elements).filter(element => {
      // Additional check to ensure element is actually visible and focusable
      return element.offsetParent !== null &&
             !element.hasAttribute('disabled') &&
             element.tabIndex !== -1;
    });
  }, [modalRef]);

  // =========================================
  // Handle Tab Keypress
  // =========================================
  const handleTabKey = useCallback((event) => {
    if (!isActive || event.key !== 'Tab') return;

    const focusableElements = getFocusableElements();

    // Handle edge case: no focusable elements
    if (focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Shift + Tab (backwards)
    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    }
    // Tab (forwards)
    else {
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }, [isActive, getFocusableElements]);

  // =========================================
  // Focus First Element
  // =========================================
  const focusFirstElement = useCallback(() => {
    const focusableElements = getFocusableElements();

    if (focusableElements.length > 0) {
      // Small delay to ensure modal is fully rendered
      requestAnimationFrame(() => {
        focusableElements[0].focus();
      });
    }
  }, [getFocusableElements]);

  // =========================================
  // Activation Effect
  // =========================================
  useEffect(() => {
    if (isActive && modalRef.current) {
      // Store the currently focused element
      previouslyFocusedElement.current = document.activeElement;

      // Move focus to the first focusable element in the modal
      focusFirstElement();

      // Add tab key listener
      document.addEventListener('keydown', handleTabKey);

      return () => {
        // Remove tab key listener
        document.removeEventListener('keydown', handleTabKey);

        // Return focus to the previously focused element
        if (returnFocus && previouslyFocusedElement.current) {
          // Check if the element still exists in the DOM
          if (document.contains(previouslyFocusedElement.current)) {
            previouslyFocusedElement.current.focus();
          }
        }

        previouslyFocusedElement.current = null;
      };
    }
  }, [isActive, modalRef, handleTabKey, focusFirstElement, returnFocus]);

  return null;
};

export default useFocusTrap;
